import React, { useState, useRef, useEffect } from "react";
import { DndContext } from "@dnd-kit/core";
import DraggableBeacon from "./DraggableBeacon";
import DraggableDataTag from "./DraggableDataTag";
import DraggableAgentNode from "./DraggableAgentNode";
import colorUtils from "../../../../Utils/ColorUtil";

function isVideoUrl(url) {
  if (!url) return false;
  const lower = url.toLowerCase();
  return (
    lower.includes(".mp4") || lower.includes(".webm") ||
    lower.includes(".mov") || lower.includes(".mkv") ||
    lower.includes("youtube.com") || lower.includes("youtu.be") ||
    lower.includes("wistia.com") || lower.includes("vimeo.com")
  );
}

export default function FactoryDrag({ asset, setAsset }) {
  const mainRef = useRef(null);
  const [selectedFactory, setSelectedFactory] = useState(null);

  function handleDragEnd(event) {
    const id = event.active.id;
    const mainWidth = mainRef.current.offsetWidth;
    const mainHeight = mainRef.current.offsetHeight;
    const deltaXPct = (event.delta.x / mainWidth) * 100;
    const deltaYPct = (event.delta.y / mainHeight) * 100;

    // Check if dragged item is a data tag or agent (position-based coords)
    const dataTag = selectedFactory.dataTags?.find((x) => x.id === id);
    const agent = !dataTag ? selectedFactory.agents?.find((x) => x.id === id) : null;

    if (dataTag || agent) {
      const item = dataTag || agent;
      const newX = (item.position?.x ?? 50) + deltaXPct;
      const newY = (item.position?.y ?? 50) + deltaYPct;
      const updated = { ...item, position: { ...item.position, x: newX, y: newY } };

      if (dataTag) {
        const updatedTags = selectedFactory.dataTags.map((t) => t.id === id ? updated : t);
        setSelectedFactory((prev) => ({ ...prev, dataTags: updatedTags }));
        setAsset((prev) => ({ ...prev, dataTags: updatedTags }));
      } else {
        const updatedAgents = selectedFactory.agents.map((a) => a.id === id ? updated : a);
        setSelectedFactory((prev) => ({ ...prev, agents: updatedAgents }));
        setAsset((prev) => ({ ...prev, agents: updatedAgents }));
      }
      return;
    }

    // Standard beacon or AI feature (Left/Top string-based coords)
    const beacon =
      selectedFactory.Children.find((x) => x.id === id) ||
      selectedFactory?.AiFeatures?.find((x) => x.id === id);
    if (!beacon) return;

    const newLeft = parseFloat(beacon.Left.replace("%", "")) + deltaXPct;
    const newTop = parseFloat(beacon.Top.replace("%", "")) + deltaYPct;
    beacon.Left = `${newLeft}%`;
    beacon.Top = `${newTop}%`;

    const _beacons = selectedFactory.Children.map((x) =>
      x.id === beacon.id ? beacon : x
    );
    const _features = (selectedFactory?.AiFeatures || []).map((x) =>
      x.id === beacon.id ? beacon : x
    );
    setSelectedFactory((prev) => ({ ...prev, Children: _beacons, AiFeatures: _features }));
    setAsset((prev) => ({ ...prev, Children: _beacons, AiFeatures: _features }));
  }

  useEffect(() => {
    if (asset.Type === "factory") {
      setSelectedFactory(asset);
    }
  }, [asset]);

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <div
        id="main-container"
        style={{
          display: "flex",
          flexDirection: "row",
          width: "fit-content",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        className="navStandardGradient"
      >
        <div
          id="factorycontainer"
          style={{ display: "flex", justifyContent: "center", overflow: "hidden" }}
        >
          <div
            id="factory"
            ref={mainRef}
            style={{
              backgroundColor: "transparent",
              position: "relative",
              zIndex: 0,
              height: "fit-content",
              maxWidth: "100%",
            }}
          >
            <div
              id="beaconlayer"
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                zIndex: 1,
              }}
            >
              {/* Standard beacons (Children) */}
              {selectedFactory?.Children?.map((child) => {
                const catcolor = child.color || "#4E79A7";
                const textColor = colorUtils.isColorDark(catcolor)
                  ? "#FFFFFF"
                  : "#000000";

                return (
                  <DraggableBeacon
                    id={child.id}
                    key={child.id}
                    data={{ ...child, catcolor }}
                    variant="asset"
                    styles={{
                      position: "absolute",
                      left: child.Left,
                      top: child.Top,
                      color: textColor,
                    }}
                  >
                    {child.Title}
                  </DraggableBeacon>
                );
              })}

              {/* AI Features */}
              {selectedFactory?.AiFeatures?.map((child) => {
                const catcolor = child.color || "#4E79A7";
                const textColor = colorUtils.isColorDark(catcolor)
                  ? "#FFFFFF"
                  : "#000000";

                return (
                  <DraggableBeacon
                    id={child.id}
                    key={child.id}
                    data={{ ...child, catcolor }}
                    variant="ai"
                    styles={{
                      position: "absolute",
                      left: child.Left,
                      top: child.Top,
                      color: textColor,
                    }}
                  >
                    {child.Title}
                  </DraggableBeacon>
                );
              })}

              {/* Data Tags - production-accurate preview */}
              {selectedFactory?.dataTags?.map((tag) => (
                <DraggableDataTag key={tag.id} tag={tag} />
              ))}

              {/* Agent Nodes - production-accurate preview */}
              {selectedFactory?.agents?.map((agent) => (
                <DraggableAgentNode key={agent.id} agent={agent} />
              ))}
            </div>

            {(() => {
              const mediaSrc = selectedFactory?.img || "https://tulip.widen.net/content/hy3p5vlugh/webp/no%20image.webp";
              const imageScale = asset?.imageScale || 1;
              const mediaStyle = {
                maxWidth: "100%",
                maxHeight: "800px",
                opacity: 1,
                backgroundColor: "transparent",
                transform: `scale(${imageScale})`,
                transformOrigin: "center center",
                transition: "transform 0.2s ease",
              };
              if (isVideoUrl(mediaSrc)) {
                return (
                  <video
                    src={mediaSrc}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{ ...mediaStyle, pointerEvents: "none" }}
                  />
                );
              }
              return (
                <img
                  src={mediaSrc}
                  alt={selectedFactory?.Title ? `${selectedFactory.Title} preview` : "Factory preview"}
                  style={mediaStyle}
                />
              );
            })()}
          </div>
        </div>
      </div>
    </DndContext>
  );
}
